import React, { useState, useEffect } from "react";
import "./CreateVendorModal.css";
import { createVendor } from "../services/api";

const CreateVendorModal = ({ closeModal, updateVendorData }) => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [productCategory, setProductCategory] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Close modal on Esc key
  useEffect(() => {
    const handleEsc = (e) => e.key === "Escape" && closeModal();
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [closeModal]);

  // Close modal on overlay click
  const handleOverlayClick = (e) => {
    if (e.target.classList.contains("modal-overlay")) closeModal();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
  
    // --- Frontend Quick Validation (for instant feedback)
    if (!fullName || !email || !mobileNumber || !businessName || !productCategory || !password) {
      setErrorMsg("All fields are required.");
      return;
    }
  
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMsg("Please include a valid email.");
      return;
    }
  
    if (!/^\d{10,15}$/.test(mobileNumber)) {
      setErrorMsg("Enter a valid phone number (10–15 digits).");
      return;
    }
  
    if (password.length < 6) {
      setErrorMsg("Password must be at least 6 characters.");
      return;
    }
  
    setLoading(true);
  
    try {
      const response = await createVendor(
        fullName,
        email,
        mobileNumber,
        businessName,
        productCategory,
        password
      );
  
      // ✅ If backend successfully creates vendor
      if (response?.status === 201) {
        updateVendorData?.();
        setTimeout(() => closeModal(), 1000);
      } else {
        setErrorMsg("Failed to create vendor. Please try again.");
      }
  
    } catch (error) {
      console.error("Error creating vendor:", error);
  
      if (error.response) {
        const { status, data } = error.response;
  
        // --- 🎯 Handle backend-specific messages ---
        if (status === 400) {
          // Validation errors array from express-validator
          if (Array.isArray(data.errors) && data.errors.length > 0) {
            // Join all validation messages
            const allMessages = data.errors.map(err => err.msg).join(" • ");
            setErrorMsg(allMessages);
          }
          // If backend sent "Vendor already exists"
          else if (data.msg?.includes("exists")) {
            setErrorMsg("Vendor already exists with this email.");
          }
          else {
            setErrorMsg(data.msg || data.message || "Invalid input. Please check your details.");
          }
        } 
        else if (status === 401) {
          setErrorMsg("Unauthorized. Please log in again.");
        } 
        else if (status === 403) {
          setErrorMsg("Access denied.");
        } 
        else if (status === 404) {
          setErrorMsg("Endpoint not found. Please contact support.");
        } 
        else if (status === 500) {
          setErrorMsg("Server error. Please try again later.");
        } 
        else {
          setErrorMsg(data.message || "An unexpected error occurred.");
        }
  
      } else if (error.request) {
        // No response received (network issue)
        setErrorMsg("No response from server. Check your internet connection.");
      } else {
        // Something else (JS error)
        setErrorMsg("Unexpected error. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };
  
  

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-container">
        <div className="back-arrow" onClick={closeModal}>
          &#x2190;
        </div>
        <h1>Create New Vendor!</h1>
        <p>Start your DanDun's vendor journey today!</p>

        {errorMsg && <div className="error-msg">{errorMsg}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="full-name" className="label">Full Name</label>
              <div className="input-container">
                <input
                  type="text"
                  id="full-name"
                  placeholder="Full Name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  disabled={loading}
                />
                <span className="input-icon">&#128100;</span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email-id" className="label">Email ID</label>
              <div className="input-container">
                <input
                  type="email"
                  id="email-id"
                  placeholder="xyz@gmail.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
                <span className="input-icon">&#x2709;</span>
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="mobile-number" className="label">Mobile Number</label>
              <div className="input-container">
                <input
                  type="tel"
                  id="mobile-number"
                  placeholder="Enter Number"
                  value={mobileNumber}
                  onChange={(e) => setMobileNumber(e.target.value)}
                  disabled={loading}
                />
                <span className="input-icon">&#x260E;</span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="business-name" className="label">Business Name</label>
              <div className="input-container">
                <input
                  type="text"
                  id="business-name"
                  placeholder="Name Here"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  disabled={loading}
                />
                <span className="input-icon">&#128188;</span>
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="product-category" className="label">Product Category</label>
              <div className="input-container">
                <select
                  id="product-category"
                  className="bg-gray-300 text-black pb-2"
                  value={productCategory}
                  onChange={(e) => setProductCategory(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Select</option>
                  <option value="66b721bfa5440a3cdee3ed84">Woman</option>
                  <option value="66b721c0a5440a3cdee3edb4">Man</option>
                  <option value="66b721c1a5440a3cdee3edd4">Kids</option>
                  <option value="66b721c2a5440a3cdee3edee">Pharmacy's</option>
                  <option value="66b721c2a5440a3cdee3ee00">Furniture</option>
                  <option value="66b721c2a5440a3cdee3ee12">Luxury Authentic</option>
                </select>
                <span className="input-icon">&#9662;</span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password" className="label">Password</label>
              <div className="input-container">
                <input
                  type="password"
                  id="password"
                  placeholder="Enter Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
                <span className="input-icon">&#128274;</span>
              </div>
            </div>
          </div>

          <button type="submit" className="create-button-popup" disabled={loading}>
            {loading ? "Creating..." : "Create"}
          </button> 
        </form>
      </div>
    </div>
  );
};

export default CreateVendorModal;
